import { Funnel, Plus } from 'lucide-react';
import type { ReactNode } from 'react';
interface FilterDropdownProps {
  title: string;
  clearAllLabel: string;
  addFilterLabel: string;
  noFiltersLabel: string;
  noFiltersHintLabel: string;
  isEmpty: boolean;
  maxHeight?: number;
  topContent?: ReactNode;
  bottomContent?: ReactNode;
  children?: ReactNode;
  onAdd: () => void;
  onClearAll: () => void;
}

export function FilterDropdown({
  title,
  clearAllLabel,
  addFilterLabel,
  noFiltersLabel,
  noFiltersHintLabel,
  isEmpty,
  maxHeight,
  topContent,
  bottomContent,
  children,
  onAdd,
  onClearAll,
}: FilterDropdownProps) {
  return (
    <div
      data-testid="filter-dropdown"
      style={{ maxHeight: maxHeight ?? '80vh' }}
      className="mt-2 w-[360px] flex flex-col bg-neut-bg-alt rounded-lg shadow-xl border border-neut-border z-50 overflow-hidden"
      // The enclosing popover reads every click that reaches `body` as an outside click, and a
      // row that removes itself leaves a detached target that no longer tests as inside. No
      // click in here is ever an outside click, so it stops at the panel.
      onClick={(event) => event.stopPropagation()}
    >
      <div className="shrink-0 flex items-center justify-between p-4 border-b border-neut-border">
        <h3 className="font-semibold text-sm text-neut-prim">{title}</h3>
        <button
          className="text-xs text-neut-sec hover:text-neut-prim cursor-pointer"
          onClick={onClearAll}
        >
          {clearAllLabel}
        </button>
      </div>

      <div
        data-testid="filter-dropdown-scroll"
        className="flex-1 min-h-0 p-4 space-y-3 overflow-y-auto overflow-x-hidden"
      >
        {topContent}
        {isEmpty ? (
          <div
            data-testid="no-filters-applied"
            className="text-center py-8 text-neut-500 text-sm"
          >
            <Funnel size={32} className="mx-auto mb-2 text-neut-300" />
            <p>{noFiltersLabel}</p>
            <p className="text-xs mt-1">{noFiltersHintLabel}</p>
          </div>
        ) : (
          children
        )}
      </div>

      <div className="shrink-0 p-3 border-t border-neut-border">
        <button
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-neut-prim hover:bg-neut-hover rounded-lg transition-colors cursor-pointer"
          onClick={onAdd}
        >
          <Plus size={14} />
          <span>{addFilterLabel}</span>
        </button>
      </div>

      <div className="shrink-0">{bottomContent}</div>
    </div>
  );
}
